import { ModalLink } from "@app/shared/Modals";
import { useState, useContext, useMemo, JSX, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { cloneDeep } from "lodash";
import { ExportBenchmarkingDashboardDetailsReportModel } from "@app/exports/exportModels";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { BenchmarkingDashboardDetailsReportTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { ExportFormatType } from "@app/exports/enumerations";
import { formatDateCustom } from "@app/shared/formatDateTime";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import ExportService from "@app/exports/exportService";

export const BenchmarkingReportModal = (): JSX.Element => {
  const [reportType, setReportType] = useState<string>("csv");
  const [addressType, setAddressType] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [fileNameIsValid, setFileNameIsValid] = useState<boolean>(true);
  const [emailLink, setEmailLink] = useState<boolean>(false);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const exportService = useMemo(() => new ExportService(), []);

  const { mutate: createReport, isPending: createReportLoading } = useMutation({
    mutationFn: (payload: ExportBenchmarkingDashboardDetailsReportModel) => exportService.postExportBenchmarkingDashboardDetails(payload),
    onError: (error: unknown) => {
      const status = error !== null && typeof error === "object" && "status" in error ? (error as { status: unknown }).status : undefined;
      handleToast(true, status as string | undefined);
    },
    onSuccess: (response) => {
      if (!!response) {
        handleToast(false);
      }
    },
  });

  useEffect(() => {
    resetState()
  }, []);

  const resetState = () => {
    setReportType("csv");
    setAddressType("");
    setFileName(getDefaultReportTitle(""))
    setEmailLink(false);
  }

  const handleModalClose = () => {
    // adding a tiny timeout prevents an unnecessary red validation flicker
    // from appearing right before the modal closes
    setTimeout(() => {
      resetState();
    }, 500);
  }

  const handleToast = (isError: boolean, errorStatus?: string): void => {
    const toastText = isError ?
      `${!!errorStatus ? `${errorStatus} - ` : ""}There was an error submitting the report. Please try again, or contact your Client Services Representative.` :
      "Your report has been successfully created.";

    toast(toastText, {
      autoClose: isError ? false : 5000,
      containerId: "z-index-9999",
      type: isError ? "error" : "success",
    });
  }

  const onTitleChange = (newTitle: string): void => {
    // checks whether a report title string contains the following "invalid" characters:  / \ : . * ? " < > |
    const isValid = !/[/\\:.*\\?"<>|]/.test(newTitle);

    setFileNameIsValid(isValid);
    setFileName(newTitle);
  }

  const getDefaultReportTitle = (addressType: string): string => {
    return `Competitive_Insights_${(addressType == "include") ? "Access_Points" : "NPI"}_${formatDateCustom(new Date(), "YYYYMMDD_hhmmss")}`;
  }

  const onConfirmCreateReportClick = (): void => {
    const payload: ExportBenchmarkingDashboardDetailsReportModel = {
      emailOnSuccess: emailLink,
      exportName: fileName || getDefaultReportTitle(addressType),
      filters: cloneDeep(benchmarkingContext.getFormattedReportFiltersPayload(networkComparisonFiltersContext.displayedNetworkComparisonFilters)),
      formatType: reportType === 'csv' ? ExportFormatType.CSV : ExportFormatType.Excel,
      reportType: (addressType == "include") ? BenchmarkingDashboardDetailsReportTypeEnum.WithAddresses : BenchmarkingDashboardDetailsReportTypeEnum.WithoutAddresses,
      sharedUsers: [],
    }
    payload.filters.state.counties = null;
    createReport(payload);
    resetState();
  }

  const modalBody = (
    <div>
      <QESSelect
        label="Address Inclusion"
        onChange={(e) => {
          setAddressType(e.target.value)
          setFileName(getDefaultReportTitle(e.target.value))
        }}
        required={true}
        isValid={!!addressType}
        value={addressType}
        disabled={false}
        testId="benchmarking__dashboardDetails-address-inclusion-select" >
        <option key="0" value="null" disabled={true} > </option>
        <option key="1" value={"exclude"} >Exclude Addresses </option>
        <option key="2" value={"include"}>Include Addresses </option>
      </QESSelect>
      <QESTextInput
        type="text"
        className="mb-1"
        disabled={false}
        testId="benchmarking__dashboardDetails-file-name"
        placeholder="Input Name"
        label="File Name"
        value={fileName}
        errorText={`Your report title may not contain any of the following characters: \u002f \u005c \u003a \u002e \u002a \u003f \u0022 \u003c \u003e |`}
        isValid={fileNameIsValid}
        required={true}
        onChange={(e) => onTitleChange(e.target.value)}
      />
      <QESSelect
        label="File Type"
        onChange={(e) => setReportType(e.target.value)}
        required={true}
        isValid={!!reportType}
        value={reportType}
        disabled={false}
        testId="benchmarking__dashboardDetails-file-type-select" >
        <option key="0" value="null" disabled={true} >  </option>
        <option key="1" value={"csv"} > Comma-Delimited Text File(.csv) </option>
        <option key="2" value={"excel"} > Microsoft Excel Workbook(.xlsx) </option>
      </QESSelect>
      <QESCheckbox
        label={"Yes, please email me a link to the report when ready"}
        testId={"benchmarking__dashboardDetails-email-link"}
        checked={emailLink}
        onChange={() => (setEmailLink(!emailLink))}
      />
    </div>
  );

  return (
    <ModalLink
      content={{
        body: modalBody,
        cancelButtonText: "Cancel",
        confirmButtonAction: () => {
          onConfirmCreateReportClick()
        },
        confirmButtonDisable: (!fileNameIsValid || createReportLoading || fileName === "" || addressType === ""),
        confirmButtonText: "Confirm",
        title: "Report Details"
      }}
      classNames="me-3 qes-button-primary"
      testId="benchmarking-dashboard--dashboard-details--create-report"
      hideBodyScroll={true}
      onClose={handleModalClose}
    >
      Create Report
    </ModalLink>
  );
}

export default BenchmarkingReportModal